import type {
	BootstrapResult,
	PartySettings,
	PartyCommand,
	PartySummary,
	PlayerProfile,
	PlayerSelection,
	QuickMatchRequest,
	RoomConnection,
	SkinDefinition,
	TeamBattleRequest,
	PublicTournamentStanding,
	PublicTournamentBracket,
	TournamentAdminAction,
	TournamentAdminAuditEntry,
	TournamentAdminDetails,
	TournamentAdminVersion,
	TournamentDefinition,
	TournamentResolution,
	TournamentSummary,
} from "@games-monorepo/yak-trails-core";

import { apiClient } from "./client";

function endpoint(apiBase: string, path: string): string {
	const base = apiBase.trim().replace(/\/$/, "");
	return base ? `${base}${path}` : path;
}

function sessionOptions(gameSessionToken: string) {
	return {
		requiresAuth: false as const,
		headers: { Authorization: `Bearer ${gameSessionToken}` },
	};
}

export const yakTrailsApi = {
	bootstrap(apiBase: string, credential?: string): Promise<BootstrapResult> {
		return apiClient.post<BootstrapResult>(
			endpoint(apiBase, "/v1/session/bootstrap"),
			{},
			credential
				? { requiresAuth: false, headers: { Authorization: `Bearer ${credential}` } }
				: undefined,
		);
	},

	getSkins(apiBase: string): Promise<{ skins: SkinDefinition[] }> {
		return apiClient.get(endpoint(apiBase, "/v1/catalog/skins"), { requiresAuth: false });
	},

	selectSkin(apiBase: string, token: string, skinId: string, profileVersion: number): Promise<PlayerProfile> {
		return apiClient.patch(
			endpoint(apiBase, "/v1/profile/skin"),
			{ skinId, profileVersion },
			sessionOptions(token),
		);
	},

	startRankedSolo(apiBase: string, token: string, selection: PlayerSelection): Promise<RoomConnection> {
		return apiClient.post(endpoint(apiBase, "/v1/matches/solo"), selection, sessionOptions(token));
	},

	joinQuickMatch(apiBase: string, token: string, request: QuickMatchRequest): Promise<RoomConnection> {
		return apiClient.post(endpoint(apiBase, "/v1/matches/quick"), request, sessionOptions(token));
	},

	startTeamBattle(apiBase: string, token: string, request: TeamBattleRequest): Promise<RoomConnection> {
		return apiClient.post(endpoint(apiBase, "/v1/matches/team"), request, sessionOptions(token));
	},

	listTournaments(apiBase: string, token: string): Promise<TournamentSummary[]> {
		return apiClient
			.get<{ tournaments: TournamentSummary[] }>(
				endpoint(apiBase, "/v1/tournaments"),
				sessionOptions(token),
			)
			.then((result) => result.tournaments);
	},

	checkInTournament(apiBase: string, token: string, tournamentId: string): Promise<{ checkedIn: boolean }> {
		return apiClient.post(
			endpoint(apiBase, `/v1/tournaments/${encodeURIComponent(tournamentId)}/check-in`),
			{},
			sessionOptions(token),
		);
	},

	resolveTournamentRoom(apiBase: string, token: string, tournamentId: string): Promise<TournamentResolution> {
		return apiClient.post(
			endpoint(apiBase, `/v1/tournaments/${encodeURIComponent(tournamentId)}/match`),
			{},
			sessionOptions(token),
		);
	},

	getTournamentStandings(apiBase: string, token: string, tournamentId: string): Promise<PublicTournamentStanding[]> {
		return apiClient
			.get<{ standings: PublicTournamentStanding[] }>(
				endpoint(apiBase, `/v1/tournaments/${encodeURIComponent(tournamentId)}/standings`),
				sessionOptions(token),
			)
			.then((result) => result.standings);
	},

	getTournamentBracket(apiBase: string, token: string, tournamentId: string): Promise<PublicTournamentBracket> {
		return apiClient.get(
			endpoint(apiBase, `/v1/tournaments/${encodeURIComponent(tournamentId)}/bracket`),
			sessionOptions(token),
		);
	},

	listAdminTournaments(apiBase: string, token: string): Promise<TournamentSummary[]> {
		return apiClient
			.get<{ tournaments: TournamentSummary[] }>(
				endpoint(apiBase, "/v1/admin/tournaments"),
				sessionOptions(token),
			)
			.then((result) => result.tournaments);
	},

	async getTournamentAdminDetails(apiBase: string, token: string, tournamentId: string): Promise<TournamentAdminDetails> {
		const encodedId = encodeURIComponent(tournamentId);
		const [details, versions, audit] = await Promise.all([
			apiClient.get<{ tournament: TournamentAdminDetails["tournament"] }>(
				endpoint(apiBase, `/v1/admin/tournaments/${encodedId}`),
				sessionOptions(token),
			),
			apiClient.get<{ versions: TournamentAdminVersion[] }>(
				endpoint(apiBase, `/v1/admin/tournaments/${encodedId}/versions`),
				sessionOptions(token),
			),
			apiClient.get<{ audit: TournamentAdminAuditEntry[] }>(
				endpoint(apiBase, `/v1/admin/tournaments/${encodedId}/audit`),
				sessionOptions(token),
			),
		]);
		return { tournament: details.tournament, versions: versions.versions, audit: audit.audit };
	},

	previewTournamentDefinition(
		apiBase: string,
		token: string,
		definition: Partial<TournamentDefinition>,
		entrantCount: number,
	): Promise<unknown> {
		return apiClient.post(
			endpoint(apiBase, "/v1/admin/tournaments/preview"),
			{ definition, entrantCount },
			sessionOptions(token),
		);
	},

	createTournamentDraft(
		apiBase: string,
		token: string,
		definition: Partial<TournamentDefinition>,
		reason: string,
	): Promise<TournamentDefinition> {
		return apiClient
			.post<{ tournament: { definition: TournamentDefinition } }>(
				endpoint(apiBase, "/v1/admin/tournaments"),
				{ definition, reason },
				sessionOptions(token),
			)
			.then((result) => result.tournament.definition);
	},

	mutateTournament(
		apiBase: string,
		token: string,
		tournamentId: string,
		action: TournamentAdminAction,
		reason: string,
		scheduledAt?: string,
	): Promise<unknown> {
		return apiClient.post(
			endpoint(apiBase, `/v1/admin/tournaments/${encodeURIComponent(tournamentId)}/${action}`),
			{ reason, ...(scheduledAt ? { scheduledAt } : {}) },
			sessionOptions(token),
		);
	},

	overrideTournamentAdvancement(
		apiBase: string,
		token: string,
		tournamentId: string,
		stageId: string,
		entrantIds: string[],
		reason: string,
	): Promise<{ advanced: number; targetStageId: string }> {
		return apiClient.post(
			endpoint(apiBase, `/v1/admin/tournaments/${encodeURIComponent(tournamentId)}/stages/${encodeURIComponent(stageId)}/override`),
			{ entrantIds, reason },
			sessionOptions(token),
		);
	},

	createParty(apiBase: string, token: string, settings: PartySettings): Promise<PartySummary> {
		return apiClient.post(endpoint(apiBase, "/v1/parties"), settings, sessionOptions(token));
	},

	getParty(apiBase: string, token: string, slug: string): Promise<PartySummary> {
		return apiClient.get(endpoint(apiBase, `/v1/parties/${encodeURIComponent(slug)}`), sessionOptions(token));
	},

	joinParty(apiBase: string, token: string, slug: string): Promise<PartySummary> {
		return apiClient.post(endpoint(apiBase, `/v1/parties/${encodeURIComponent(slug)}/join`), {}, sessionOptions(token));
	},

	resolvePartyRoom(apiBase: string, token: string, slug: string): Promise<RoomConnection> {
		return apiClient.post(endpoint(apiBase, `/v1/parties/${encodeURIComponent(slug)}/match`), {}, sessionOptions(token));
	},

	commandParty(apiBase: string, token: string, slug: string, command: PartyCommand): Promise<PartySummary> {
		return apiClient.post(endpoint(apiBase, `/v1/parties/${encodeURIComponent(slug)}/command`), command, sessionOptions(token));
	},
};
